// src/services/categorySuggestionsService.js
import categoryMappings from '../data/categoryMappings.json';
import { cleanTransactionName, getCategorizationKey } from '../utils/cleanTransactionName';

/**
 * Serviço para fornecer sugestões de categorização baseadas em mapeamentos globais
 */

/**
 * Normaliza a descrição da transação para correspondência usando a função de limpeza
 * @param {Object} transaction - Transação
 * @returns {string} - Descrição normalizada
 */
const normalizeDescription = (transaction) => {
  if (!transaction || !transaction.description) return "";
  
  const bankName = transaction.bankInfo?.org || "";
  
  // Usar a função de limpeza para normalizar
  return getCategorizationKey(transaction.description, bankName);
};

/**
 * Verifica se uma descrição corresponde a alguma das variações conhecidas
 * @param {string} description - Descrição da transação
 * @param {Array} variations - Variações conhecidas
 * @returns {number} - Score de similaridade (0-1)
 */
const calculateSimilarity = (description, variations) => {
  if (!variations || !variations.length) return 0;
  
  const normalizedDesc = description.toLowerCase();
  let maxScore = 0;
  
  variations.forEach(variation => {
    const normalizedVar = variation.toLowerCase();
    
    // Correspondência exata
    if (normalizedDesc === normalizedVar) {
      maxScore = Math.max(maxScore, 1.0);
      return;
    }
    
    // Contém a variação
    if (normalizedDesc.includes(normalizedVar)) {
      maxScore = Math.max(maxScore, 0.8);
      return;
    }
    
    // Variação contém a descrição
    if (normalizedVar.includes(normalizedDesc)) {
      maxScore = Math.max(maxScore, 0.7);
      return;
    }
    
    // Similaridade de Levenshtein simplificada
    const similarity = 1 - levenshteinDistance(normalizedDesc, normalizedVar) / 
                       Math.max(normalizedDesc.length, normalizedVar.length);
    maxScore = Math.max(maxScore, similarity);
  });
  
  return maxScore;
};

/**
 * Calcula a distância de Levenshtein entre duas strings
 * @param {string} str1 - Primeira string
 * @param {string} str2 - Segunda string
 * @returns {number} - Distância
 */
const levenshteinDistance = (str1, str2) => {
  const track = Array(str2.length + 1).fill(null).map(() =>
    Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) {
    track[0][i] = i;
  }
  
  for (let j = 0; j <= str2.length; j++) {
    track[j][0] = j;
  }
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1, // deletion
        track[j - 1][i] + 1, // insertion
        track[j - 1][i - 1] + indicator // substitution
      );
    }
  }
  
  return track[str2.length][str1.length];
};

/**
 * Obtém sugestões para uma transação
 * @param {Object} transaction - Transação
 * @returns {Array} - Lista de sugestões ordenadas por confiança
 */
export const getSuggestionsForTransaction = (transaction) => {
  if (!transaction || !transaction.description) return [];
  
  const normalizedDesc = normalizeDescription(transaction);
  const amount = transaction.amount || 0;
  const suggestions = [];
  
  console.log(`Buscando sugestões para: "${transaction.description}"`, `Normalizado: "${normalizedDesc}"`);
  
  // Verificar correspondência direta
  if (categoryMappings[normalizedDesc]) {
    const mapping = categoryMappings[normalizedDesc];
    
    // Verificar compatibilidade de sinal
    if ((!mapping.positiveOnly || amount > 0) && 
        (!mapping.negativeOnly || amount < 0)) {
      console.log(`Encontrada correspondência direta: ${mapping.category}`);
      suggestions.push({
        ...mapping,
        score: 1.0,
        matchType: 'exact'
      });
    }
  }
  
  // Buscar mapeamentos similares
  Object.entries(categoryMappings).forEach(([key, mapping]) => {
    // Pular mapeamento exato já processado
    if (key === normalizedDesc) return;
    
    // Verificar compatibilidade de sinal
    if ((mapping.positiveOnly && amount <= 0) || 
        (mapping.negativeOnly && amount > 0)) {
      return;
    }
    
    // Verificar similaridade com a chave
    let similarity = key.includes(normalizedDesc) ? 0.8 : 0;
    
    // Verificar similaridade com variações se disponível
    if (mapping.variations && mapping.variations.length > 0) {
      const varSimilarity = calculateSimilarity(normalizedDesc, mapping.variations);
      similarity = Math.max(similarity, varSimilarity);
    }
    
    // Verificar similaridade com o nome limpo se disponível
    if (mapping.cleanedName) {
      const cleanedSimilarity = calculateSimilarity(normalizedDesc, [mapping.cleanedName]);
      similarity = Math.max(similarity, cleanedSimilarity);
    }
    
    // Adicionar sugestão se a similaridade for significativa
    if (similarity > 0.6) {
      console.log(`Encontrada correspondência similar: ${mapping.category} (score: ${similarity})`);
      suggestions.push({
        ...mapping,
        score: similarity * (mapping.confidence || 1.0),
        matchType: 'similar'
      });
    }
  });
  
  console.log(`Total de sugestões encontradas: ${suggestions.length}`);
  
  // Ordenar sugestões por score
  return suggestions.sort((a, b) => b.score - a.score);
};

/**
 * Carrega estatísticas das sugestões disponíveis
 * @returns {Object} - Estatísticas
 */
export const getMappingStats = () => {
  const stats = {
    totalMappings: Object.keys(categoryMappings).length,
    byCategory: {},
    byBank: {},
    highConfidence: 0
  };
  
  Object.values(categoryMappings).forEach(mapping => {
    // Contar por categoria
    const mainCategory = mapping.categoryPath.split('.')[0];
    stats.byCategory[mainCategory] = (stats.byCategory[mainCategory] || 0) + 1;
    
    // Contar alta confiança
    if (mapping.confidence > 0.8) {
      stats.highConfidence++;
    }
  });
  
  return stats;
};